import React, { FC, useCallback, useState } from 'react';
import { LegacyAppointment } from 'src/interfaces/api/Collaborative';
import useCollaborative from 'src/hooks/api/useCollaborative';
import EntityTable from 'src/components/Table';
import { TableColumn } from 'src/components/Table/EntityTable';
import appointmentsState from 'src/recoil/atoms/appointments';
import { Order } from 'src/interfaces/Table';
import data_criticity from 'src/data/criticity';
import moment from 'moment';
import { MdWarning as WarningIcon } from '@react-icons/all-files/md/MdWarning';
import Ticket from 'src/interfaces/api/Ticket';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import useTicket from 'src/hooks/api/useTicket';
import { useSetRecoilState } from 'recoil';
import { ticketState } from 'src/recoil/selectors/tickets';
import { IconButton } from '@material-ui/core';
import { MdDelete as DeleteIcon } from '@react-icons/all-files/md/MdDelete';

interface Props {
  hidden?: boolean;
  ticketId: Ticket['id'];
  appointmentsIds: LegacyAppointment['id'][];
}

const columns: TableColumn<LegacyAppointment>[] = [
  {
    id: 'title',
    type: 'text',
    label: 'Titre',
    link: (appointment: LegacyAppointment) => `/dashboard/appointment/${appointment.id}`,
    renderValue: (appointment: LegacyAppointment) => {
      const meetingDate = moment(appointment.dateMeeting);

      const outdated = !appointment.closed && meetingDate <= moment();
      if (!outdated) {
        if (!appointment.closed && meetingDate <= moment().add(48, 'hours')) {
          return (
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <WarningIcon size={16} color='#ff9800' title='RDV dans moins de 48h' /> {appointment.title}
            </span>
          );
        }
      }
      return <span>{appointment.title}</span>;
    },
  },
  {
    id: 'closed',
    type: 'bool',
    label: 'Status',
    renderValue: (appointment: LegacyAppointment) => (appointment.closed ? 'Effectué' : 'Non-effectué'),
  },
  {
    id: 'dateMeeting',
    type: 'datetime',
    label: 'Date rdv',
    sortable: true,
  },
  {
    id: 'criticity',
    type: 'text',
    label: 'Criticité',
    sortable: true,
    renderValue: (appointment: LegacyAppointment) => data_criticity[appointment.criticity]?.fr,
  },
];

const AppointmentTable: FC<Props> = ({ ticketId, hidden, appointmentsIds }) => {
  const setTicket = useSetRecoilState(ticketState(ticketId));
  const { getAppointments } = useCollaborative();
  const { removeAppointment } = useTicket();
  const [appointmentToRemove, setAppointmentToRemove] = useState<LegacyAppointment['id']>();

  const handleRemoveAppointment = async () => {
    if (appointmentToRemove) {
      const ticket = await removeAppointment(ticketId, appointmentToRemove);
      setTicket(ticket);
      setAppointmentToRemove(undefined);
    }
  };

  const handleGetAppointment = useCallback(
    async (page: number, limit: number, orderBy: keyof LegacyAppointment, order: Order) => {
      return await getAppointments(appointmentsIds || [], page, limit, orderBy, order);
    },
    [appointmentsIds]
  );

  return (
    <>
      <EntityTable
        hidden={hidden}
        columns={columns}
        defaultOrderBy='dateMeeting'
        atomState={appointmentsState}
        actions={(appointment) => (
          <IconButton size='small' onClick={() => setAppointmentToRemove(appointment.id)}>
            <DeleteIcon />
          </IconButton>
        )}
        fetchEntitiesCallback={handleGetAppointment}
      />

      <ConfirmationDialog
        title='Attention'
        confirmationText='Supprimer'
        description='Vous allez supprimer ce rendez-vous. Êtes-vous sûr ?'
        onClose={() => setAppointmentToRemove(undefined)}
        open={!!appointmentToRemove}
        onValidate={handleRemoveAppointment}
      />
    </>
  );
};

export default React.memo(AppointmentTable, (prevProps, nextProps) => {
  return prevProps.appointmentsIds === nextProps.appointmentsIds && prevProps.hidden === nextProps.hidden;
});
